import { useState, useEffect, useContext } from "react";
import {
  doc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";
import { db } from "../../firebase";
import { AuthContext } from "../../AuthProvider";

// Admin UIDs for elevated access
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];

const ENABLE_LOGGING = false;

// localStorage keys for persistence
const STORAGE_KEYS = {
  LAST_SELECTED_TEAM: 'ai-sdr-last-selected-team',
  USER_TEAM_PREFERENCE: 'ai-sdr-user-team-preference'
};

const log = (...args) => {
  if (ENABLE_LOGGING) {
    console.log("[useTeamAccess]", ...args);
  }
};

const useTeamAccess = () => {
  const { user, isAuthChecking } = useContext(AuthContext);

  // State
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [accessState, setAccessState] = useState("loading"); // "loading", "no-auth", "no-teams", "team-selector", "ready"

  // Computed values
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  // Helper function to get stored team preference
  const getStoredTeamPreference = () => {
    if (!user) return null;
    try {
      const stored = localStorage.getItem(`${STORAGE_KEYS.USER_TEAM_PREFERENCE}-${user.uid}`);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn("Error reading stored team preference:", error);
      return null;
    }
  };

  // Helper function to store team preference
  const storeTeamPreference = (teamId) => {
    if (!user || !teamId) return;
    try {
      localStorage.setItem(
        `${STORAGE_KEYS.USER_TEAM_PREFERENCE}-${user.uid}`, 
        JSON.stringify({
          teamId,
          timestamp: Date.now(),
          userUid: user.uid
        })
      );
      log("Stored team preference:", teamId);
    } catch (error) {
      console.warn("Error storing team preference:", error);
    }
  };

  // Helper function to select a team and update state
  const selectTeamInternal = (team) => {
    setSelectedTeamId(team.id);
    setSelectedTeam(team);
    setAccessState("ready");
    storeTeamPreference(team.id);
    log("Team selected:", team);
  };

  // Fetch teams where user is owner/creator/reader
  useEffect(() => {
    const fetchUserTeams = async () => {
      if (!user) {
        setTeamsLoading(false);
        setAccessState("no-auth");
        return;
      }

      try {
        setTeamsLoading(true);
        setAccessState("loading");
        log("Fetching user teams...");

        const teamsRef = collection(db, "teams");
        const ownerQ = query(
          teamsRef,
          where("owners", "array-contains", user.uid)
        );
        const creatorQ = query(
          teamsRef,
          where("creators", "array-contains", user.email)
        );
        const readerQ = query(
          teamsRef,
          where("readers", "array-contains", user.email)
        );

        const [ownSnap, creSnap, readSnap] = await Promise.all([
          getDocs(ownerQ),
          getDocs(creatorQ),
          getDocs(readerQ),
        ]);

        const map = new Map();
        ownSnap.forEach((d) =>
          map.set(d.id, { id: d.id, ...d.data(), role: "owner" })
        );
        creSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "creator" });
        });
        readSnap.forEach((d) => {
          if (!map.has(d.id))
            map.set(d.id, { id: d.id, ...d.data(), role: "reader" });
        });

        const userTeams = Array.from(map.values());
        setTeams(userTeams);
        log("Found teams:", userTeams);

        // Determine access state and auto-selection logic
        if (userTeams.length === 0) {
          setAccessState("no-teams");
        } else {
          // Try to restore previously selected team
          const storedPreference = getStoredTeamPreference();
          let teamToSelect = null;

          if (storedPreference && storedPreference.teamId) {
            // Check if stored team still exists and user has access
            teamToSelect = userTeams.find(t => t.id === storedPreference.teamId);
            if (teamToSelect) {
              log("Restored previous team selection:", teamToSelect);
            }
          }

          // If no stored preference or team not found, select first team
          if (!teamToSelect) {
            teamToSelect = userTeams[0];
            log("No valid stored preference, selecting first team:", teamToSelect);
          }

          // Auto-select the team
          selectTeamInternal(teamToSelect);
        }
      } catch (err) {
        console.error("Error fetching teams:", err);
        toast.error("Failed to load teams");
        setAccessState("no-teams");
      } finally {
        setTeamsLoading(false);
      }
    };

    if (!isAuthChecking && user) {
      fetchUserTeams();
    } else if (!isAuthChecking && !user) {
      setTeamsLoading(false);
      setAccessState("no-auth");
    }
  }, [user, isAuthChecking]);

  // Team selection handler for external use
  const selectTeam = (team) => {
    if (!team || !team.id) {
      console.warn("Invalid team provided to selectTeam:", team);
      return;
    }
    
    // Verify the team exists in user's teams list
    const validTeam = teams.find(t => t.id === team.id);
    if (!validTeam) {
      console.warn("Team not found in user's teams:", team.id);
      toast.error("You don't have access to this team");
      return;
    }

    selectTeamInternal(validTeam);
    toast.success(`Switched to "${validTeam.name}"`);
  };

  // Switch to team by ID (for dropdown usage)
  const switchToTeam = (teamId) => {
    if (!teamId) return;
    
    const team = teams.find(t => t.id === teamId);
    if (team) {
      selectTeam(team);
    } else {
      console.warn("Team not found by ID:", teamId);
      toast.error("Team not found");
    }
  };

  // Clear team selection (return to selection screen)
  const clearTeamSelection = () => {
    setSelectedTeamId(null);
    setSelectedTeam(null);
    setAccessState(teams.length > 1 ? "team-selector" : "no-teams");
    
    // Clear stored preference
    if (user) {
      try {
        localStorage.removeItem(`${STORAGE_KEYS.USER_TEAM_PREFERENCE}-${user.uid}`);
      } catch (error) {
        console.warn("Error clearing team preference:", error);
      }
    }
    
    log("Team selection cleared");
  };

  // Force team selection screen (useful for settings)
  const showTeamSelector = () => {
    if (teams.length > 1) {
      setSelectedTeamId(null);
      setSelectedTeam(null);
      setAccessState("team-selector");
      log("Showing team selector");
    }
  };

  // Check team permissions
  const checkTeamPermissions = async (teamId) => {
    if (!user || !teamId) return null;

    try {
      const teamRef = doc(db, "teams", teamId);
      const teamDoc = await getDoc(teamRef);

      if (!teamDoc.exists()) {
        return { exists: false };
      }

      const teamData = teamDoc.data();
      const userHasOwnerAccess =
        user && teamData.owners && teamData.owners.includes(user.uid);
      const userHasCreatorAccess =
        user && teamData.creators && teamData.creators.includes(user.email);
      const userHasReaderAccess =
        user && teamData.readers && teamData.readers.includes(user.email);

      return {
        exists: true,
        teamData,
        access: {
          isOwner: userHasOwnerAccess,
          isCreator: userHasCreatorAccess,
          isReader: userHasReaderAccess,
          hasAnyAccess:
            userHasOwnerAccess || userHasCreatorAccess || userHasReaderAccess,
        },
      };
    } catch (err) {
      console.error("Error checking team permissions:", err);
      return { exists: false, error: err.message };
    }
  };

  // Refresh teams (useful after team changes)
  const refreshTeams = async () => {
    if (user && !isAuthChecking) {
      log("Refreshing teams...");
      // Re-fetch teams by triggering the effect
      const currentTeamId = selectedTeamId;
      setTeamsLoading(true);
      
      // The useEffect will handle the refresh
      // We'll restore selection if the current team still exists
      setTimeout(() => {
        if (currentTeamId && teams.find(t => t.id === currentTeamId)) {
          // Current team still exists, keep it selected
          log("Keeping current team selected after refresh");
        }
      }, 100);
    }
  };

  return {
    // State
    teams,
    selectedTeamId,
    selectedTeam,
    teamsLoading,
    accessState,

    // Computed
    isAdmin,
    isReady: accessState === "ready",
    needsTeamSelection: accessState === "team-selector",
    hasNoTeams: accessState === "no-teams",
    needsAuth: accessState === "no-auth",
    isLoading: accessState === "loading" || isAuthChecking,
    hasMultipleTeams: teams.length > 1,

    // Actions
    selectTeam,
    switchToTeam,
    clearTeamSelection,
    showTeamSelector,
    refreshTeams,
    checkTeamPermissions,

    // Helper methods
    getSelectedTeamRole: () => selectedTeam?.role || null,
    canManageTeam: () => selectedTeam?.role === "owner",
    canEditContent: () => ["owner", "creator"].includes(selectedTeam?.role),
    canReadContent: () => ["owner", "creator", "reader"].includes(selectedTeam?.role),
  };
};

export default useTeamAccess;
